import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  BarChart3, Brain, CheckCircle, Clock, DollarSign,
  Layers, Zap, ChevronDown, ChevronUp, Download, Image,
  Wheat, Truck, Warehouse, ShoppingCart, Shield, Globe,
  Thermometer, LineChart, Cpu, Package,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const HARVEST_DIVISION_META = {
  DI: { name: "Demand Intelligence", icon: LineChart, color: "#3b82f6" },
  FW: { name: "Farm-to-Warehouse", icon: Truck, color: "#22c55e" },
  WI: { name: "Warehouse & Inventory", icon: Warehouse, color: "#f59e0b" },
  LM: { name: "Last-Mile Distribution", icon: Package, color: "#8b5cf6" },
  RM: { name: "Retail & Markdown", icon: ShoppingCart, color: "#ec4899" },
  FS: { name: "Food Safety & Traceability", icon: Shield, color: "#ef4444" },
  SN: { name: "Supplier Network", icon: Globe, color: "#06b6d4" },
  CC: { name: "Cold Chain Engineering", icon: Thermometer, color: "#0ea5e9" },
  AR: { name: "Analytics & Reporting", icon: BarChart3, color: "#a855f7" },
  PO: { name: "Platform Orchestration", icon: Cpu, color: "#f97316" },
};

function getHarvestPrefix(nodeId) {
  if (!nodeId) return null;
  for (const prefix of ["DI", "FW", "WI", "LM", "RM", "FS", "SN", "CC", "AR", "PO"]) {
    if (nodeId.startsWith(prefix + "_")) return prefix;
  }
  if (nodeId === "HARVEST_MASTER" || nodeId === "HARVEST_EXEC") return "MASTER";
  return null;
}

export function isHarvestExecution(executionDetail) {
  if (!executionDetail) return false;
  const name = (executionDetail.swarm_name || executionDetail.template_name || "").toLowerCase();
  if (name.includes("harvest")) return true;
  const results = executionDetail.agent_results || {};
  const nodeIds = Object.keys(results);
  if (nodeIds.length < 50) return false;
  const harvestPrefixes = nodeIds.filter((id) => getHarvestPrefix(id) !== null);
  return harvestPrefixes.length > nodeIds.length * 0.5;
}

function DivisionCard({ prefix, meta, results, totalInDivision }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = meta.icon;
  const completed = results.length;
  const errors = results.filter((r) => r.error).length;
  const cost = results.reduce((sum, r) => sum + (r.cost || 0), 0);
  const tokens = results.reduce((sum, r) => sum + (r.tokens || 0), 0);
  const avgLatency = completed > 0 ? Math.round(results.reduce((sum, r) => sum + (r.latency_ms || 0), 0) / completed) : 0;
  const pct = totalInDivision > 0 ? Math.round((completed / totalInDivision) * 100) : 0;
  const media = results.filter((r) => r.media_type && r.media_url);

  return (
    <div
      className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] overflow-hidden transition-all hover:border-opacity-60"
      style={{ borderColor: `${meta.color}30` }}
      data-testid={`harvest-division-${prefix}`}
    >
      <button onClick={() => setExpanded(!expanded)} className="w-full p-4 text-left">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${meta.color}18` }}>
            <Icon className="w-4.5 h-4.5" style={{ color: meta.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[var(--qn-foreground)]">{meta.name}</div>
            <div className="text-[10px] text-[var(--qn-foreground-muted)]">{completed} / {totalInDivision} agents</div>
          </div>
          <div className="text-right">
            {errors > 0 ? (
              <Badge variant="destructive" className="text-[9px]">{errors} errors</Badge>
            ) : completed === totalInDivision ? (
              <Badge className="text-[9px] bg-amber-600">Complete</Badge>
            ) : (
              <Badge variant="outline" className="text-[9px]">{pct}%</Badge>
            )}
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-[var(--qn-foreground-muted)]" /> : <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-muted)]" />}
        </div>
        <div className="w-full h-1.5 rounded-full bg-[var(--qn-surface)] overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="h-full rounded-full"
            style={{ background: meta.color }}
          />
        </div>
        <div className="flex items-center gap-4 mt-2.5 text-[10px] text-[var(--qn-foreground-muted)]">
          <span>{tokens.toLocaleString()} tokens</span>
          <span>${cost.toFixed(4)}</span>
          <span>avg {avgLatency}ms</span>
          {media.length > 0 && <span>{media.length} media</span>}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 pb-4 border-t border-[var(--qn-border)]/30 space-y-2">
              {media.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {media.map((r) => (
                    <div key={r.node_id} className="rounded-lg overflow-hidden border border-[var(--qn-border)]">
                      {r.media_type === "image" ? (
                        <img src={`${API}${r.media_url}`} alt={r.label} className="w-full h-32 object-cover" loading="lazy" />
                      ) : (
                        <video src={`${API}${r.media_url}`} controls className="w-full h-32 object-cover" preload="metadata" />
                      )}
                      <div className="px-2 py-1 text-[9px] text-[var(--qn-foreground-muted)] truncate">
                        {r.label}
                        {r.model_display_name && <span className="ml-1 text-amber-400">via {r.model_display_name}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 space-y-1.5 max-h-[200px] overflow-y-auto">
                {results.slice(0, 5).map((r) => (
                  <div key={r.node_id} className="text-[10px] p-2 rounded-lg bg-[var(--qn-surface)]/30">
                    <span className="font-medium text-[var(--qn-foreground)]">{r.label}</span>
                    <span className="text-[var(--qn-foreground-muted)] ml-1">— {(r.output || "").slice(0, 120)}...</span>
                  </div>
                ))}
                {results.length > 5 && (
                  <div className="text-[10px] text-[var(--qn-foreground-muted)] text-center py-1">
                    + {results.length - 5} more agents
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function HarvestExecutiveDashboard({ executionDetail }) {
  const results = Object.values(executionDetail.agent_results || {});
  const name = executionDetail.swarm_name || executionDetail.template_name || "HARVEST Execution";

  const divisionGroups = {};
  const masterResults = [];

  results.forEach((r) => {
    const prefix = getHarvestPrefix(r.node_id);
    if (prefix === "MASTER" || !prefix) {
      masterResults.push(r);
    } else {
      if (!divisionGroups[prefix]) divisionGroups[prefix] = [];
      divisionGroups[prefix].push(r);
    }
  });

  const isMaster = name.toLowerCase().includes("1,000") || name.toLowerCase().includes("1000") || results.length > 500;
  const totalPerDivision = isMaster ? 100 : 100;

  const totalAgents = results.length;
  const completedAgents = results.filter((r) => !r.error).length;
  const errorAgents = results.filter((r) => r.error).length;
  const totalCost = results.reduce((sum, r) => sum + (r.cost || 0), 0);
  const totalTokens = results.reduce((sum, r) => sum + (r.tokens || 0), 0);
  const allMedia = results.filter((r) => r.media_type && r.media_url);
  const divisionCount = Object.keys(divisionGroups).length;
  const completedDivisions = Object.entries(divisionGroups).filter(
    ([, arr]) => arr.length >= totalPerDivision * 0.9
  ).length;
  const overallPct = totalAgents > 0 ? Math.round((completedAgents / totalAgents) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="harvest-executive-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-outfit font-bold text-amber-400 tracking-tight flex items-center gap-2">
            <Wheat className="w-5 h-5" /> HARVEST Executive Dashboard
          </h2>
          <p className="text-xs text-[var(--qn-foreground-muted)] mt-0.5">{name} — {executionDetail.execution_id}</p>
        </div>
        <Badge
          className={`text-xs ${executionDetail.status === "completed" ? "bg-amber-600" : executionDetail.status === "completed_with_errors" ? "bg-orange-600" : "bg-red-600"}`}
        >
          {executionDetail.status}
        </Badge>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="harvest-stats-bar">
        {[
          { label: "Total Agents", value: totalAgents.toLocaleString(), icon: Brain, color: "#3b82f6" },
          { label: "Completion", value: `${overallPct}%`, icon: CheckCircle, color: "#22c55e" },
          { label: "Divisions", value: `${completedDivisions}/${divisionCount}`, icon: Layers, color: "#a855f7" },
          { label: "Total Cost", value: `$${totalCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: DollarSign, color: "#eab308" },
          { label: "Tokens Used", value: totalTokens.toLocaleString(), icon: BarChart3, color: "#14b8a6" },
          { label: "Duration", value: executionDetail.duration_ms ? `${(executionDetail.duration_ms / 1000).toFixed(1)}s` : "--", icon: Clock, color: "#f97316" },
        ].map((stat) => (
          <div key={stat.label} className="p-3 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid={`harvest-stat-${stat.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center gap-2 mb-1">
              <stat.icon className="w-3.5 h-3.5" style={{ color: stat.color }} />
              <span className="text-[10px] text-[var(--qn-foreground-muted)] uppercase tracking-wider">{stat.label}</span>
            </div>
            <div className="text-lg font-outfit font-bold text-[var(--qn-foreground)]">{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-amber-400">Overall Progress</span>
          <span className="text-xs text-[var(--qn-foreground-muted)]">{completedAgents} / {totalAgents} agents {errorAgents > 0 && `(${errorAgents} errors)`}</span>
        </div>
        <div className="w-full h-3 rounded-full bg-[var(--qn-surface)] overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${overallPct}%` }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400"
          />
        </div>
      </div>

      <div>
        <h3 className="text-xs font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-3">
          Supply Chain Divisions ({divisionCount})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(HARVEST_DIVISION_META).map(([prefix, meta]) => {
            const divResults = divisionGroups[prefix] || [];
            if (divResults.length === 0 && !isMaster) return null;
            return (
              <DivisionCard
                key={prefix}
                prefix={prefix}
                meta={meta}
                results={divResults}
                totalInDivision={totalPerDivision}
              />
            );
          })}
        </div>
      </div>

      {allMedia.length > 0 && (
        <div data-testid="harvest-media-gallery">
          <h3 className="text-xs font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-3 flex items-center gap-2">
            <Image className="w-3.5 h-3.5" /> Generated Media ({allMedia.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {allMedia.map((r) => (
              <div key={r.node_id} className="rounded-xl overflow-hidden border border-[var(--qn-border)] bg-[var(--qn-card)] group">
                {r.media_type === "image" ? (
                  <img src={`${API}${r.media_url}`} alt={r.label} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                ) : (
                  <video src={`${API}${r.media_url}`} controls className="w-full h-40 object-cover bg-black" preload="metadata" />
                )}
                <div className="p-2">
                  <div className="text-[10px] font-medium text-[var(--qn-foreground)] truncate">{r.label}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[9px] text-[var(--qn-foreground-muted)]">
                      ${(r.cost || 0).toFixed(4)}
                      {r.model_display_name && <span className="ml-1 text-amber-400">via {r.model_display_name}</span>}
                    </span>
                    <a href={`${API}${r.media_url}`} target="_blank" rel="noopener noreferrer" className="text-[9px] text-[var(--qn-primary)] hover:underline flex items-center gap-0.5">
                      <Download className="w-2.5 h-2.5" /> Download
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {masterResults.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-3">
            Executive Summary Agents
          </h3>
          <div className="space-y-2">
            {masterResults.map((r) => (
              <div key={r.node_id} className="p-3 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)]">
                <div className="flex items-center gap-2 mb-1">
                  <Wheat className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-xs font-medium text-[var(--qn-foreground)]">{r.label}</span>
                  {r.error && <Badge variant="destructive" className="text-[9px]">Error</Badge>}
                </div>
                <pre className="text-[10px] text-[var(--qn-foreground-muted)] whitespace-pre-wrap font-mono max-h-[150px] overflow-y-auto">{r.output}</pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
